import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ConfirmDialog } from './ConfirmDialog';
import { Plus, Save, X, User, AlertCircle } from 'lucide-react';
import { AddAdultRequest, Child, ChildRelationshipInput } from '../types/family.types';
import { Gender, RelationshipType, UserStatus } from '../types/enums';
import { useFamilyMutations } from '../hooks/useFamilyMutations';
import { getGenderOptions, getMonthOptions } from '../utils/form-options.utils';

interface AddAdultCardProps {
  familyId: string;
  children?: Child[];
  familyStatus?: UserStatus;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  gender: Gender;
  birthYear?: number;
  birthMonth?: number;
  childRelationships: Record<string, RelationshipType>;
}

const createInitialFormData = (): FormData => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  gender: Gender.PREFER_NOT_TO_SAY,
  birthYear: undefined,
  birthMonth: undefined,
  childRelationships: {},
});

export const AddAdultCard = ({ familyId, children = [], familyStatus }: AddAdultCardProps) => {
  const { t } = useTranslation('registration');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState<FormData>(createInitialFormData());
  const [formErrors, setFormErrors] = useState<Map<string, string>>(new Map());

  const { addAdult } = useFamilyMutations(familyId);

  const getErrorClass = (id: string) => (formErrors.has(id) ? 'border-red-500 bg-red-50/10' : '');

  const getErrorMessage = (id: string) => formErrors.get(id) || '';

  const scrollToError = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const errorBanner = document.getElementById('error-banner-adult');
      if (errorBanner) {
        errorBanner.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        element.focus({ preventScroll: true });
      }, 300);
    }
  };

  const validateForm = (): { isValid: boolean; firstErrorId?: string } => {
    const errors = new Map<string, string>();
    let firstErrorId: string | undefined;

    const addError = (id: string, message: string) => {
      errors.set(id, message);
      if (!firstErrorId) firstErrorId = id;
    };

    // First Name validation
    if (!formData.firstName.trim()) {
      addError('add-adult-firstName', t('validation.enterNameFor', { label: t('labels.primaryAdult') }));
    } else if (formData.firstName.trim().length < 2) {
      addError('add-adult-firstName', t('validation.nameTooShort'));
    } else if (formData.firstName.length > 100) {
      addError('add-adult-firstName', t('validation.nameTooLong'));
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      addError('add-adult-lastName', t('validation.enterNameFor', { label: t('labels.primaryAdult') }));
    } else if (formData.lastName.trim().length < 2) {
      addError('add-adult-lastName', t('validation.nameTooShort'));
    } else if (formData.lastName.length > 100) {
      addError('add-adult-lastName', t('validation.nameTooLong'));
    }

    // Email validation
    if (!formData.email.trim()) {
      addError('add-adult-email', t('validation.emailRequired', { label: t('labels.primaryAdult') }));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      addError('add-adult-email', t('validation.invalidEmailFormat'));
    }

    // Phone validation
    if (!formData.phone.trim()) {
      addError(
        'add-adult-phone',
        t('validation.phoneError', { label: t('labels.primaryAdult'), error: t('validation.phoneRequired') }),
      );
    } else {
      const cleanPhone = formData.phone.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        addError('add-adult-phone', t('validation.phoneInvalidFormat'));
      } else if (cleanPhone.length > 11) {
        addError('add-adult-phone', t('validation.phoneInvalidFormat'));
      }
    }

    setFormErrors(errors);
    return { isValid: errors.size === 0, firstErrorId };
  };

  const handleSave = async () => {
    const { isValid, firstErrorId } = validateForm();
    if (!isValid) {
      if (firstErrorId) scrollToError(firstErrorId);
      return;
    }

    if (familyStatus === UserStatus.PENDING) {
      const apiErrors = new Map<string, string>();
      apiErrors.set('add-adult-firstName', t('toast.familyPendingCannotAddMembers'));
      setFormErrors(apiErrors);
      scrollToError('add-adult-firstName');
      return;
    }
    // Convert childRelationships object to array format expected by API
    const childRelationships: ChildRelationshipInput[] = Object.entries(formData.childRelationships)
      .filter(([, relationshipType]) => relationshipType !== undefined)
      .map(([childId, relationshipType]) => ({
        childId,
        relationshipType,
      }));

    const request: AddAdultRequest = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      gender: formData.gender,
      birthYear: formData.birthYear,
      birthMonth: formData.birthMonth,
      childRelationships: childRelationships.length > 0 ? childRelationships : undefined,
    };

    try {
      await addAdult.mutateAsync(request);
      setFormData(createInitialFormData());
      setFormErrors(new Map());
      setIsExpanded(false);
    } catch (error) {
      const apiErrors = new Map<string, string>();
      const errorMsg =
        error instanceof Error ? error.message : String((error as Record<string, unknown>)?.message ?? '');

      const phoneErrorCodes: Record<string, string> = {
        INVALID_AREA_CODE_PREFIX: 'validation.phoneAreaCodePrefix',
        INVALID_CANADIAN_AREA_CODE: 'validation.phoneCanadianAreaCode',
        INVALID_EXCHANGE_CODE: 'validation.phoneExchangeCodePrefix',
        INVALID_PATTERN: 'validation.phonePattern',
        INVALID_E164: 'validation.phoneE164',
      };

      if (errorMsg.includes('First name') || errorMsg.includes('firstName')) {
        apiErrors.set('add-adult-firstName', errorMsg);
      } else if (errorMsg.includes('Last name') || errorMsg.includes('lastName')) {
        apiErrors.set('add-adult-lastName', errorMsg);
      } else if (errorMsg.includes('Email') || errorMsg.includes('email')) {
        apiErrors.set('add-adult-email', errorMsg);
      } else if (errorMsg.includes('Phone') || errorMsg.includes('phone')) {
        let phoneMsg = errorMsg;
        for (const [code, i18nKey] of Object.entries(phoneErrorCodes)) {
          if (errorMsg.includes(code)) {
            phoneMsg = t(i18nKey);
            break;
          }
        }
        apiErrors.set('add-adult-phone', phoneMsg);
      } else if (errorMsg.includes('Birth year') || errorMsg.includes('birthYear')) {
        apiErrors.set('add-adult-birthYear', errorMsg);
      } else if (errorMsg.includes('Birth month') || errorMsg.includes('birthMonth')) {
        apiErrors.set('add-adult-birthMonth', errorMsg);
      } else if (errorMsg.includes('age') || errorMsg.includes('14 years')) {
        apiErrors.set('add-adult-birthYear', t('validation.adultTooYoung'));
      } else if (errorMsg.includes('already exists')) {
        apiErrors.set('add-adult-email', t('validation.emailAlreadyExists'));
      } else if (errorMsg.includes('inactive') || errorMsg.includes('not active')) {
        apiErrors.set('add-adult-firstName', t('toast.familyPendingCannotAddMembers'));
      } else if (errorMsg.includes('more than')) {
        apiErrors.set('add-adult-firstName', t('toast.familyTooManyAdults'));
      } else if (errorMsg) {
        apiErrors.set('add-adult-phone', errorMsg);
      } else {
        apiErrors.set('add-adult-firstName', t('toast.failedToAddAdult'));
      }

      setFormErrors(apiErrors);
      if (apiErrors.size > 0) {
        const firstErrorId = apiErrors.keys().next().value as string;
        scrollToError(firstErrorId);
      }
    }
  };

  const hasChanges = () => {
    return formData.firstName !== '' || formData.lastName !== '' || formData.email !== '' || formData.phone !== '';
  };

  const isValid = () => {
    return (
      formData.firstName.trim().length >= 2 &&
      formData.lastName.trim().length >= 2 &&
      formData.email.trim() !== '' &&
      formData.phone.trim() !== ''
    );
  };

  const handleCancelClick = () => {
    if (hasChanges()) {
      setShowConfirmDialog(true);
    } else {
      setIsExpanded(false);
    }
  };

  const handleConfirmDiscard = () => {
    setFormData(createInitialFormData());
    setShowConfirmDialog(false);
    setIsExpanded(false);
  };

  const relationshipOptions = [
    { value: RelationshipType.PARENT, label: t('relationshipOptions.parent') },
    { value: RelationshipType.GRANDPARENT, label: t('relationshipOptions.grandparent') },
    { value: RelationshipType.RELATIVE, label: t('relationshipOptions.relative') },
    { value: RelationshipType.AUTHORIZED, label: t('relationshipOptions.authorizedGuardian') },
    { value: RelationshipType.EMERGENCY, label: t('relationshipOptions.emergency') },
  ];

  const handleChildRelationshipChange = (childId: string, relationshipType: RelationshipType | '') => {
    setFormData((prev) => {
      const newRelationships = { ...prev.childRelationships };
      if (relationshipType === '') {
        delete newRelationships[childId];
      } else {
        newRelationships[childId] = relationshipType;
      }
      return { ...prev, childRelationships: newRelationships };
    });
  };

  const genderOptions = getGenderOptions(t);
  const monthOptions = getMonthOptions(t);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, i) => currentYear - i);

  if (!isExpanded) {
    return (
      <Card
        className="border-dashed border-2 border-[var(--color-neutral-300)] hover:border-[var(--color-primary-400)] cursor-pointer transition-colors"
        onClick={() => setIsExpanded(true)}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-2 text-[var(--color-neutral-500)] hover:text-[var(--color-primary-600)]">
            <Plus className="h-5 w-5" />
            <span className="font-medium">{t('buttons.addAdult')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="ring-2 ring-[var(--color-primary-500)]">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-[var(--color-secondary-700)]" />
            <span className="font-medium text-sm sm:text-base">{t('buttons.addAdult')}</span>
          </div>
          <div className="flex gap-2 self-end sm:self-auto">
            <Button variant="outline" size="sm" onClick={handleCancelClick} disabled={addAdult.isPending}>
              <X className="h-4 w-4 mr-1" />
              {t('profile.cancel')}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={addAdult.isPending || !isValid()}>
              <Save className="h-4 w-4 mr-1" />
              {addAdult.isPending ? t('profile.adding') : t('buttons.add')}
            </Button>
          </div>
        </div>

        {formErrors.size > 0 && (
          <div
            id="error-banner-adult"
            className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 rounded-r-lg mb-4 shadow-md sticky top-0 z-10"
            role="alert"
            aria-live="polite"
          >
            <div className="flex gap-2 sm:gap-3">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-red-800 mb-2">
                  {t('validation.adultValidationFailed')}
                </h3>
                <div className="text-xs sm:text-sm text-red-700 space-y-1">
                  {Array.from(formErrors.values())
                    .slice(0, 5)
                    .map((error, idx) => (
                      <div key={idx} className="flex items-start gap-1.5 break-words">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>{error}</span>
                      </div>
                    ))}
                  {formErrors.size > 5 && (
                    <div className="text-xs sm:text-sm text-red-700 mt-2">
                      {t('validation.andMore', { count: formErrors.size - 5 })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={showConfirmDialog}
          title={t('profile.discardChanges')}
          message={t('profile.discardConfirm')}
          onConfirm={handleConfirmDiscard}
          onCancel={() => setShowConfirmDialog(false)}
        />

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div>
              <Label htmlFor="add-adult-firstName" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.firstName')}
              </Label>
              <Input
                id="add-adult-firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder={t('placeholders.firstName')}
                className={`mt-1.5 text-base ${getErrorClass('add-adult-firstName')}`}
              />
              {getErrorMessage('add-adult-firstName') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-adult-firstName')}</p>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="add-adult-lastName" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.lastName')}
              </Label>
              <Input
                id="add-adult-lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder={t('placeholders.lastName')}
                className={`mt-1.5 text-base ${getErrorClass('add-adult-lastName')}`}
              />
              {getErrorMessage('add-adult-lastName') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-adult-lastName')}</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div>
              <Label htmlFor="add-adult-email" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.email')}
              </Label>
              <Input
                id="add-adult-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('placeholders.email')}
                className={`mt-1.5 text-base ${getErrorClass('add-adult-email')}`}
              />
              {getErrorMessage('add-adult-email') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-adult-email')}</p>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="add-adult-phone" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.phone')}
              </Label>
              <Input
                id="add-adult-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t('placeholders.phone')}
                className={`mt-1.5 text-base ${getErrorClass('add-adult-phone')}`}
              />
              {getErrorMessage('add-adult-phone') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-adult-phone')}</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
            <div className="sm:col-span-2 md:col-span-1">
              <Label htmlFor="add-adult-gender" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.gender')}
              </Label>
              <select
                id="add-adult-gender"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value as Gender })}
                className={`mt-1.5 w-full px-3 py-2.5 border rounded-md bg-white text-base ${getErrorClass('add-adult-gender')}`}
              >
                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {getErrorMessage('add-adult-gender') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-adult-gender')}</p>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="add-adult-birthMonth" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.birthMonth')}
              </Label>
              <select
                id="add-adult-birthMonth"
                value={formData.birthMonth || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    birthMonth: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                className={`mt-1.5 w-full px-3 py-2.5 border rounded-md bg-white text-base ${getErrorClass('add-adult-birthMonth')}`}
              >
                <option value="">{t('placeholders.selectMonth')}</option>
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              {getErrorMessage('add-adult-birthMonth') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-adult-birthMonth')}</p>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="add-adult-birthYear" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.birthYear')}
              </Label>
              <select
                id="add-adult-birthYear"
                value={formData.birthYear || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    birthYear: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                className={`mt-1.5 w-full px-3 py-2.5 border rounded-md bg-white text-base ${getErrorClass('add-adult-birthYear')}`}
              >
                <option value="">{t('placeholders.selectYear')}</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {getErrorMessage('add-adult-birthYear') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-adult-birthYear')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Child Relationships Section */}
          {children.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-[var(--color-neutral-200)]">
              <Label className="text-sm font-medium">{t('profile.relationshipToChildren')}</Label>
              <p className="text-xs text-[var(--color-neutral-500)]">{t('profile.relationshipHelper')}</p>
              <div className="space-y-2">
                {children.map((child) => (
                  <div
                    key={child.id}
                    className="flex items-center justify-between gap-2 p-2 bg-[var(--color-neutral-50)] rounded-lg"
                  >
                    <span className="text-sm text-[var(--color-neutral-700)] truncate">
                      {child.firstName} {child.lastName}
                    </span>
                    <select
                      value={formData.childRelationships[child.id] || ''}
                      onChange={(e) => handleChildRelationshipChange(child.id, e.target.value as RelationshipType | '')}
                      className="text-sm px-2 py-1 border rounded-md bg-white w-full min-w-[120px] max-w-[140px]"
                    >
                      <option value="">{t('profile.notSpecified')}</option>
                      {relationshipOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
